import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Tooltip as LeafletTooltip,
  useMap,
} from 'react-leaflet';
import L, { LatLngExpression, PathOptions } from 'leaflet';
import api from '../../services/api';
import { formatAddressForDisplay } from '../AddressAutocomplete';
import { US_MAP_BOUNDS, US_MAP_CENTER, US_MAP_DEFAULT_ZOOM } from '../../utils/usAddressRules';

interface AddressMapProps {
  address: {
    fullAddress: string;
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
    choroplethGeoJson?: any;
    choroplethValueProperty?: string;
    choroplethLabel?: string;
    choroplethMetricLabel?: string;
  };
  neighborhoodTractCode?: string;
  evaluationNeighborhood?: string;
  locationContext?: {
    city?: string;
    county?: string;
    state?: string;
  };
}

const mapContainerStyle = {
  width: '100%',
  height: '850px',
  borderRadius: '8px',
  position: 'relative' as const,
};

const defaultCenter = {
  lat: US_MAP_CENTER[0],
  lng: US_MAP_CENTER[1],
};

const addressMarkerIcon = L.icon({
  iconUrl: '/images/marker.svg',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

type HoverInfo = {
  x: number;
  y: number;
  neighborhood: string;
  metricLabel: string;
  value: number | null;
  loadingNeighborhood: boolean;
};

const neighborhoodCache = new Map<string, string>();

function getFeatureCentroid(feature: any): { lat: number; lng: number } | null {
  const geom = feature?.geometry;
  if (!geom) return null;

  const ringCentroid = (ring: number[][]) => {
    if (!Array.isArray(ring) || ring.length === 0) return null;
    let lat = 0;
    let lng = 0;
    let count = 0;
    for (const coord of ring) {
      if (!Array.isArray(coord) || coord.length < 2) continue;
      lng += Number(coord[0]);
      lat += Number(coord[1]);
      count += 1;
    }
    if (!count) return null;
    return { lat: lat / count, lng: lng / count };
  };

  if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
    const [lng, lat] = geom.coordinates;
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  if (geom.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
    return ringCentroid(geom.coordinates[0]);
  }
  if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates?.[0]?.[0])) {
    return ringCentroid(geom.coordinates[0][0]);
  }
  return null;
}

function getNeighborhoodFromProps(props: Record<string, unknown>): string {
  const direct = String(props.neighborhood || props.NTAName || props.community || '').trim();
  if (direct) return direct;

  const rawName = String(props.NAME || props.name || '').trim();
  if (rawName && !/^census tract/i.test(rawName)) {
    const first = rawName.split(';')[0]?.trim();
    if (first && !/^census tract/i.test(first)) return first;
  }
  return '';
}

function formatAreaFallback(
  props: Record<string, unknown>,
  locationContext?: { city?: string; county?: string; state?: string },
): string {
  const tract = String(props.tractCode || props.TRACT || props.TRACTCE || '')
    .replace(/^0+/, '')
    .trim();
  const city = String(locationContext?.city || '').trim();
  const county = String(locationContext?.county || '').replace(/\s+County$/i, '').trim();
  const state = String(locationContext?.state || '').trim();

  if (tract && city) return `${city} · Tract ${tract}`;
  if (tract && county) return `${county} · Tract ${tract}`;
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (county) return county;
  return '';
}

async function reverseGeocodeNeighborhood(lat: number, lng: number, cacheKey: string): Promise<string> {
  const cached = neighborhoodCache.get(cacheKey);
  if (cached) return cached;

  const { data } = await api.get<{ neighborhood?: string }>('/address/reverse-geocode', {
    params: { lat, lng },
  });
  const trimmed = String(data?.neighborhood || '').trim();
  if (trimmed) neighborhoodCache.set(cacheKey, trimmed);
  return trimmed;
}

function formatChoroplethValue(valueProperty: string, value: number): string {
  const prop = valueProperty.toLowerCase();
  if (prop.includes('income') || prop.includes('value') || prop.includes('rent')) {
    return `$${Math.round(value).toLocaleString()}`;
  }
  if (
    prop.includes('rate') ||
    prop.includes('yield') ||
    prop.includes('proxy') ||
    prop.includes('unemployment')
  ) {
    return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  }
  return Number(value).toLocaleString();
}

const FitToGeoJson: React.FC<{ geojson?: any; fallbackCenter: LatLngExpression }> = ({
  geojson,
  fallbackCenter,
}) => {
  const map = useMap();

  useEffect(() => {
    try {
      if (geojson?.features?.length) {
        const layer = L.geoJSON(geojson as any);
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [24, 24], maxZoom: 12 });
          return;
        }
      }
      map.setView(fallbackCenter, map.getZoom() || 12);
    } catch {
      // ignore
    }
  }, [map, geojson, fallbackCenter]);

  return null;
};

const AddressMap: React.FC<AddressMapProps> = ({
  address,
  neighborhoodTractCode,
  evaluationNeighborhood,
  locationContext,
}) => {
  const mapWrapRef = useRef<HTMLDivElement>(null);
  const hoverRequestRef = useRef(0);

  const center: LatLngExpression = useMemo(
    () =>
      address.coordinates
        ? [address.coordinates.lat, address.coordinates.lng]
        : [defaultCenter.lat, defaultCenter.lng],
    [address.coordinates?.lat, address.coordinates?.lng],
  );

  const displayAddress = useMemo(
    () =>
      formatAddressForDisplay(address.fullAddress, {
        street: address.street,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
      }),
    [address.fullAddress, address.street, address.city, address.state, address.zipCode],
  );

  const choroplethProp = address.choroplethValueProperty || 'value';
  const choroplethLabel = address.choroplethLabel || 'Heatmap';
  const metricLabel = address.choroplethMetricLabel || choroplethLabel;
  const geoJsonRef = useRef<L.GeoJSON | null>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);

  const heatPalette = useMemo(
    () => ['#fff1f2', '#fecdd3', '#fda4af', '#fb7185', '#ef4444'],
    [],
  );

  const choroplethBreaks = useMemo(() => {
    const features: any[] = address.choroplethGeoJson?.features || [];
    const vals: number[] = [];
    for (const f of features) {
      const raw = f?.properties?.[choroplethProp];
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(n)) vals.push(n);
    }
    vals.sort((a, b) => a - b);
    if (vals.length === 0) return null;

    const makeIntervalBreaks = (min: number, max: number) => {
      if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
      if (min === max) return [min, min, min, min];
      const step = (max - min) / 5;
      return [min + step, min + step * 2, min + step * 3, min + step * 4];
    };

    if (vals.length < 5) {
      const min = vals[0];
      const max = vals[vals.length - 1];
      return makeIntervalBreaks(min, max);
    }

    const q = (p: number) => {
      const idx = Math.floor((vals.length - 1) * p);
      return vals[idx];
    };
    const quantileBreaks = [q(0.2), q(0.4), q(0.6), q(0.8)];

    const isStrictlyIncreasing =
      quantileBreaks[0] < quantileBreaks[1] &&
      quantileBreaks[1] < quantileBreaks[2] &&
      quantileBreaks[2] < quantileBreaks[3];

    if (!isStrictlyIncreasing) {
      return makeIntervalBreaks(vals[0], vals[vals.length - 1]);
    }

    return quantileBreaks;
  }, [address.choroplethGeoJson, choroplethProp]);

  const choroplethMinMax = useMemo(() => {
    const features: any[] = address.choroplethGeoJson?.features || [];
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const f of features) {
      const raw = f?.properties?.[choroplethProp];
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(n)) continue;
      if (n < min) min = n;
      if (n > max) max = n;
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    return { min, max };
  }, [address.choroplethGeoJson, choroplethProp]);

  const legendBuckets = useMemo(() => {
    const colors = heatPalette;
    if (!choroplethMinMax) return null;
    if (!choroplethBreaks) {
      return [
        {
          label: `${choroplethMinMax.min.toLocaleString()}–${choroplethMinMax.max.toLocaleString()}`,
          color: colors[2],
        },
      ];
    }
    const [b1, b2, b3, b4] = choroplethBreaks;
    const min = choroplethMinMax.min;
    const max = choroplethMinMax.max;
    const fmt = (n: number) => (Number.isFinite(n) ? n.toLocaleString() : '—');
    return [
      { label: `${fmt(min)}–${fmt(b1)}`, color: colors[0] },
      { label: `${fmt(b1)}–${fmt(b2)}`, color: colors[1] },
      { label: `${fmt(b2)}–${fmt(b3)}`, color: colors[2] },
      { label: `${fmt(b3)}–${fmt(b4)}`, color: colors[3] },
      { label: `${fmt(b4)}+`, color: colors[4] },
      { label: `${fmt(max)}`, color: 'transparent' },
    ].filter((b) => b.color !== 'transparent');
  }, [choroplethBreaks, choroplethMinMax, heatPalette]);

  const getFillColor = (value: number | undefined) => {
    if (value == null || !Number.isFinite(value)) return '#fca5a5';
    const colors = heatPalette;
    if (!choroplethBreaks) return colors[2];
    const [b1, b2, b3, b4] = choroplethBreaks;
    if (value <= b1) return colors[0];
    if (value <= b2) return colors[1];
    if (value <= b3) return colors[2];
    if (value <= b4) return colors[3];
    return colors[4];
  };

  const choroplethStyle = (feature: any): PathOptions => {
    const value = feature?.properties?.[choroplethProp];
    const base: PathOptions = {
      fillColor: getFillColor(typeof value === 'number' ? value : Number(value)),
      weight: 0,
      opacity: 0,
      color: 'rgba(0,0,0,0)',
      fillOpacity: 0.58,
    };

    const tractCode = String(
      feature?.properties?.tractCode || feature?.properties?.TRACT || feature?.properties?.TRACTCE || '',
    ).padStart(6, '0');
    const isNeighborhood =
      neighborhoodTractCode &&
      tractCode === String(neighborhoodTractCode).padStart(6, '0');

    if (isNeighborhood) {
      return {
        ...base,
        weight: 2,
        opacity: 1,
        color: '#111827',
        fillOpacity: 0.45,
      };
    }

    return base;
  };

  const resolveNeighborhood = useCallback(
    async (feature: any, tractCode: string, props: Record<string, unknown>) => {
      if (
        evaluationNeighborhood &&
        neighborhoodTractCode &&
        tractCode === String(neighborhoodTractCode).padStart(6, '0')
      ) {
        return evaluationNeighborhood;
      }

      const fromProps = getNeighborhoodFromProps(props);
      if (fromProps) return fromProps;

      const geoid = String(props.GEOID || tractCode || '');
      const geoidCached = geoid ? neighborhoodCache.get(geoid) : undefined;
      if (geoidCached) return geoidCached;

      const centroid = getFeatureCentroid(feature);
      if (!centroid) return formatAreaFallback(props, locationContext);

      try {
        const cacheKey = geoid || `${centroid.lat.toFixed(4)},${centroid.lng.toFixed(4)}`;
        const name = await reverseGeocodeNeighborhood(centroid.lat, centroid.lng, cacheKey);
        if (name) return name;
      } catch {
        // fall through to local fallback
      }

      return formatAreaFallback(props, locationContext);
    },
    [evaluationNeighborhood, locationContext, neighborhoodTractCode],
  );

  const handleFeatureHover = useCallback(
    (e: any) => {
      try {
        const wrap = mapWrapRef.current;
        const originalEvent = e?.originalEvent as MouseEvent | undefined;
        if (!wrap || !originalEvent) return;

        const rect = wrap.getBoundingClientRect();
        const x = originalEvent.clientX - rect.left;
        const y = originalEvent.clientY - rect.top;

        const f = e?.sourceTarget?.feature;
        const props = f?.properties || {};
        const tractCode = String(props.tractCode || props.TRACT || props.TRACTCE || '').padStart(6, '0');
        const raw = props?.[choroplethProp];
        const value = typeof raw === 'number' ? raw : Number(raw);

        const requestId = ++hoverRequestRef.current;
        const isEvaluatedTract =
          evaluationNeighborhood &&
          tractCode === String(neighborhoodTractCode || '').padStart(6, '0');
        const fromProps = getNeighborhoodFromProps(props);
        const syncNeighborhood = isEvaluatedTract
          ? evaluationNeighborhood
          : fromProps || '';

        setHoverInfo({
          x,
          y,
          neighborhood: syncNeighborhood || '…',
          metricLabel,
          value: Number.isFinite(value) ? value : null,
          loadingNeighborhood: !syncNeighborhood,
        });

        e?.target?.setStyle?.({
          weight: 1,
          color: '#ffffff',
          opacity: 1,
          fillOpacity: 0.55,
        });

        if (syncNeighborhood) return;

        void resolveNeighborhood(f, tractCode, props).then((neighborhood) => {
          if (hoverRequestRef.current !== requestId) return;
          setHoverInfo((prev) =>
            prev
              ? {
                  ...prev,
                  neighborhood: neighborhood || formatAreaFallback(props, locationContext) || '—',
                  loadingNeighborhood: false,
                }
              : null,
          );
        });
      } catch {
        // ignore
      }
    },
    [
      choroplethProp,
      evaluationNeighborhood,
      locationContext,
      metricLabel,
      neighborhoodTractCode,
      resolveNeighborhood,
    ],
  );

  const handleFeatureMove = useCallback((e: any) => {
    const wrap = mapWrapRef.current;
    const originalEvent = e?.originalEvent as MouseEvent | undefined;
    if (!wrap || !originalEvent) return;
    const rect = wrap.getBoundingClientRect();
    const x = originalEvent.clientX - rect.left;
    const y = originalEvent.clientY - rect.top;
    setHoverInfo((prev) => (prev ? { ...prev, x, y } : null));
  }, []);

  const handleFeatureLeave = useCallback((e: any) => {
    hoverRequestRef.current += 1;
    setHoverInfo(null);
    try {
      geoJsonRef.current?.resetStyle(e?.target);
    } catch {
      // ignore
    }
  }, []);

  const tooltipStyle = useMemo(() => {
    if (!hoverInfo) return undefined;
    const pad = 14;
    const maxLeft = Math.max(pad, (mapWrapRef.current?.clientWidth || 400) - 240);
    const maxTop = Math.max(pad, (mapWrapRef.current?.clientHeight || 400) - 120);
    return {
      left: Math.min(hoverInfo.x + pad, maxLeft),
      top: Math.min(hoverInfo.y + pad, maxTop),
    };
  }, [hoverInfo]);

  return (
    <div ref={mapWrapRef} style={mapContainerStyle}>
      {hoverInfo && tooltipStyle && (
        <div className="wti-map-hovercard wti-map-hovercard--cursor" style={tooltipStyle}>
          <div className="wti-map-hovercard-title">{choroplethLabel}</div>
          <div className="wti-map-hovercard-neighborhood">
            {hoverInfo.loadingNeighborhood ? 'Loading area…' : hoverInfo.neighborhood}
          </div>
          <div className="wti-map-hovercard-metric">
            <span className="wti-map-hovercard-metric-label">{hoverInfo.metricLabel}</span>
            <span className="wti-map-hovercard-metric-value">
              {typeof hoverInfo.value === 'number'
                ? formatChoroplethValue(choroplethProp, hoverInfo.value)
                : 'N/A'}
            </span>
          </div>
        </div>
      )}

      {legendBuckets && (
        <div className="wti-map-legend-box" aria-label="heatmap legend">
          {legendBuckets.map((b) => (
            <div key={b.label} className="wti-map-legend-row">
              <span className="wti-map-legend-swatch" style={{ background: b.color }} />
              <span className="wti-map-legend-text">{b.label}</span>
            </div>
          ))}
        </div>
      )}

      <MapContainer
        center={center}
        zoom={address.coordinates ? 12 : US_MAP_DEFAULT_ZOOM}
        minZoom={4}
        maxBounds={US_MAP_BOUNDS}
        maxBoundsViscosity={1}
        style={{ width: '100%', height: '100%', borderRadius: '8px' }}
        scrollWheelZoom
      >
        <FitToGeoJson geojson={address.choroplethGeoJson} fallbackCenter={center} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {address.choroplethGeoJson && (
          <GeoJSON
            ref={(r) => {
              geoJsonRef.current = (r as any) || null;
            }}
            data={address.choroplethGeoJson}
            style={choroplethStyle}
            eventHandlers={{
              mouseover: handleFeatureHover,
              mousemove: handleFeatureMove,
              mouseout: handleFeatureLeave,
            }}
          />
        )}

        {address.coordinates && (
          <Marker position={center} icon={addressMarkerIcon}>
            <LeafletTooltip direction="top" offset={[0, -6]}>
              {displayAddress}
            </LeafletTooltip>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default AddressMap;
